// @flow
import { Button, Checkbox, CircularProgress, Container, CssBaseline, FormControlLabel, Grid, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Formik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import routes from '../routes';
import Layout from '../component/Layout';
import { AuthContext } from '../context/auth';
import User from '../model/User';
import { isNotValidEmail, isNotValidName, isNotValidPassword, isNotValidPhone } from '../util/validator';
import ApiResponse from '../model/ApiResponse';

const privacyPolicyUrl: string = String(process.env.REACT_APP_PRIVACY_POLICY_URL);
const marketingCampaignUrl: string = String(process.env.REACT_APP_MARKETING_CAMPAIGN_URL);

const useStyles = makeStyles((theme) => ({
  paper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingInline: theme.spacing(4),
    borderRadius: theme.spacing(1.5),
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    maxWidth: '672px',
    width: '100%',
    margin: '0 auto',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: theme.palette.grey[800],
    marginBottom: theme.spacing(3),
    alignSelf: 'flex-start',
  },
  form: {
    width: '100%',
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(4, 0, 2),
    padding: theme.spacing(1.5),
    backgroundColor: '#312e81',
    '&:hover': {
      backgroundColor: '#3730a3',
    },
    fontWeight: 500,
    textTransform: 'none',
    fontSize: '0.875rem',
  },
  error: {
    color: theme.palette.error.main,
    fontSize: '0.875rem',
    marginTop: theme.spacing(0.5),
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(0.75),
      '& fieldset': {
        borderColor: theme.palette.grey[300],
      },
      '&:hover fieldset': {
        borderColor: '#6366f1',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#6366f1',
      },
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.grey[700],
      fontSize: '0.875rem',
      fontWeight: 500,
    },
  },
  checkboxContainer: {
    marginTop: theme.spacing(3),
  },
  checkboxLabel: {
    fontSize: '0.875rem',
    color: theme.palette.grey[900],
    '& a': {
      color: '#4f46e5',
      fontWeight: 500,
      textDecoration: 'none',
      '&:hover': {
        color: '#6366f1',
      },
    },
  },
}));

const Profile = () => {
  const { t } = useTranslation();
  const { user, doPost, setUser } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const classes = useStyles();

  const validate = values => {
    const errors = {};
    if (!values.name) {
      errors.name = t('Required');
    } else if (isNotValidName(values.name)) {
      errors.name = t('Invalid name');
    }
    if (!values.email) {
      errors.email = t('Required');
    } else if (isNotValidEmail(values.email)) {
      errors.email = t('Invalid email address');
    }
    if (!values.phone) {
      errors.phone = t('Required');
    } else if (isNotValidPhone(values.phone)) {
      errors.phone = t('Invalid phone');
    }
    if (values.password && isNotValidPassword(values.password)) {
      errors.password = t('Invalid password');
    }
    if (!!user && !user.hasTerms && !values.policy) {
      errors.policy = t('Required');
    }
    return errors;
  };

  const onSubmit = async (values, { setSubmitting }) => {
    const data = {
      ...values,
      privacyPolicy: values.policy,
      marketingCampaign: values.marketing,
      deviceData: navigator.userAgent,
    };
    const response: ApiResponse = await doPost('/customer/me', data);
    if (response.ok && response.data) {
      const user = User.parse(response.data);
      if (user) {
        await setUser(user);
      }
      enqueueSnackbar(t('Data saved'), {
        variant: 'success',
      });
    } else if (!response.is401Error()) {
      enqueueSnackbar(t(response.message), {
        variant: 'error',
      });
      setSubmitting(false);
    }
  };

  return (
    <Layout activeKey={routes.profile}>
      <Container component="main" maxWidth="md">
        <CssBaseline />
        <div className={classes.paper}>
          <h1 className={classes.title}>{t('My Profile')}</h1>
          {!!user && !user.hasTerms && <Grid item xs={12}>
            <h4 className={classes.error}>{t('Accept mandatory terms')}</h4>
          </Grid>}
          {!!user && user.isRetail && <Grid item xs={12}>
            {t('Your code')}: <b>{user.code}</b>
          </Grid>}
          <Formik
            initialValues={{
              name: user ? user.name : '',
              email: user ? user.email : '',
              phone: user ? user.phone : '',
              password: '',
              policy: user && user.privacyPolicy,
              marketing: user && user.marketingCampaign,
              deviceData: 'TODO',
            }}
            validate={validate}
            onSubmit={onSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <form className={classes.form} onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      autoComplete="name"
                      name="name"
                      variant="outlined"
                      required
                      fullWidth
                      id="name"
                      label={t('Name') + ' *'}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.name}
                      className={classes.textField}
                      size="small"
                    />
                    {errors.name && touched.name && <span className={classes.error}>{errors.name}</span>}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      variant="outlined"
                      required
                      fullWidth
                      id="email"
                      label={t('Email') + ' *'}
                      name="email"
                      autoComplete="email"
                      type="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      className={classes.textField}
                      size="small"
                    />
                    {errors.email && touched.email && <span className={classes.error}>{errors.email}</span>}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      variant="outlined"
                      required
                      fullWidth
                      id="phone"
                      label={t('Phone') + ' *'}
                      name="phone"
                      autoComplete="phone"
                      type="tel"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.phone}
                      className={classes.textField}
                      size="small"
                    />
                    {errors.phone && touched.phone && <span className={classes.error}>{errors.phone}</span>}
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      disabled={isSubmitting}
                      variant="outlined"
                      fullWidth
                      name="password"
                      label={t('Password')}
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.password}
                      className={classes.textField}
                      size="small"
                      placeholder="********"
                    />
                    {errors.password && touched.password && <span className={classes.error}>{errors.password}</span>}
                  </Grid>
                </Grid>
                <div className={classes.checkboxContainer}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox
                          disabled={isSubmitting}
                          checked={values.policy}
                          onChange={(e) => setFieldValue('policy', e.target.checked)}
                          name='policy'
                          color='primary'
                          size='small' />}
                        label={<span className={classes.checkboxLabel}>
                          {t('Accept')} <a href={privacyPolicyUrl} target='_blank' rel='noopener noreferrer'>
                            {t('Accept privacy policy')}*
                          </a>
                        </span>} />
                      {errors.policy && touched.policy && <span className={classes.error}>{errors.policy}</span>}
                    </Grid>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={<Checkbox
                          disabled={isSubmitting}
                          checked={values.marketing}
                          onChange={(e) => setFieldValue('marketing', e.target.checked)}
                          name='marketing'
                          color='primary'
                          size='small' />}
                        label={<span className={classes.checkboxLabel}>
                          <a href={marketingCampaignUrl} target='_blank' rel='noopener noreferrer'>
                            {t('Accept marketing campaigns')}
                          </a>
                        </span>} />
                      {errors.marketing && touched.marketing && <span className={classes.error}>{errors.marketing}</span>}
                    </Grid>
                  </Grid>
                </div>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  className={classes.submit}
                  type="submit"
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress color="inherit" size="1.2em" /> : null}
                >
                  {t('Save')}
                </Button>
              </form>
            )}
          </Formik>
        </div>
      </Container>
    </Layout>
  );
};

export default Profile;
